// ProjectForm.tsx
import { ArrowRightIcon } from '@heroicons/react/20/solid'
import { FormProvider, useForm } from 'react-hook-form'
import { languageNameToCode } from 'util/languages'
import { useState } from 'react'
import { Dropdown } from 'components/Common/V2/Dropdown'
import { TextInput } from 'components/Common/V2/TextInput'
import { Button } from 'components/Button'
import { useCreateProjectMutation, useUpdateProject } from 'hooks/projects'
import { Project } from 'lib/types/generated-types'
import { buttonClass } from './SetupLayout'
interface ProjectFormProps {
  defaultValues?: Project
  onSubmit?: (project: Project) => void
  isEditMode?: boolean
  projectId?: number
}

const ProjectForm = ({
  defaultValues,
  isEditMode = false,
  onSubmit,
  projectId,
}: ProjectFormProps) => {
  const { mutateAsync: createProject, isLoading: isCreating } =
    useCreateProjectMutation()
  const { mutateAsync: updateProject, isLoading: isUpdating } =
    useUpdateProject()

  const methods = useForm({
    defaultValues: {
      name: defaultValues?.name || 'Default Project',
      description: defaultValues?.description || '',
      language: defaultValues?.language || Object.values(languageNameToCode)[0],
    },
  })

  const [selectedLanguage, setSelectedLanguage] = useState({
    id: defaultValues?.language || Object.values(languageNameToCode)[0],
    label: defaultValues
      ? Object.keys(languageNameToCode).find(
          (key) => languageNameToCode[key] === defaultValues.language
        ) || ''
      : Object.keys(languageNameToCode)[0] || '',
  })

  const options = Object.keys(languageNameToCode).map((language) => ({
    id: languageNameToCode[language],
    value: languageNameToCode[language],
    label: language,
  }))

  const handleSubmit = async (data: any) => {
    if (isEditMode && projectId) {
      const updatedProject = await updateProject({
        projectId,
        projectToUpdate: {
          name: data['name'],
          description: data['description'],
          language: data['language'],
        },
      })
      onSubmit?.(updatedProject)
    } else {
      const project = await createProject({
        projectToInsert: {
          name: data['name'],
          language: data['language'],
        },
        addToProject: true,
      })
      onSubmit?.(project)
    }
  }

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(handleSubmit)}
        className="mt-2 flex flex-col"
      >
        <div className="flex max-w-md flex-col gap-y-6">
          <TextInput
            register={methods.register}
            name="name"
            label="Name"
            inputClassName="text-base mt-1 !py-2 !px-3"
            labelClassName="!text-sm font-bold"
            description="Choose a name that clearly represents the content you'll be importing."
            descriptionClassName="!text-sm"
            placeholder="My Project"
            rules={{
              required: 'Name is required',
            }}
          />
          {isEditMode && (
            <TextInput
              register={methods.register}
              name="description"
              label="Description"
              inputClassName="max-w-lg text-base mt-1"
              description="Use this space to highlight the main focus, style, or goals of your project so you can quickly recall its purpose."
              placeholder="My Project Description"
            />
          )}
          <div className="flex w-full flex-col gap-y-2">
            <label htmlFor="language">
              <p className="text-sm font-bold">Language</p>
              <p className="mt-1 text-sm text-gray-500">
                All uploads in this project will be transcribed and translated
                into the language you select.
              </p>
            </label>
            <div className="flex w-full">
              <Dropdown
                options={options}
                value={selectedLanguage.id}
                size="lg"
                color="white"
                className="!w-full"
                onChange={(value: string) => {
                  const option = options.find((opt) => opt.id === value)
                  if (option) {
                    setSelectedLanguage({
                      id: option.id,
                      label: option.label,
                    })
                    methods.setValue('language', option.id)
                  }
                }}
              />
            </div>
          </div>
          <div>
            <Button
              loading={isEditMode ? isUpdating : isCreating}
              icon={ArrowRightIcon}
              iconPosition="right"
              className={buttonClass}
            >
              {isEditMode ? 'Update Project' : 'Create Project'}
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  )
}

export default ProjectForm
